import { Sparkles, ArrowUpRight } from 'lucide-react';
import { Button, Modal, cn } from '@techsofit/ui';

/** Fraction of the cap at which a paid gym starts seeing the "almost full" nudge. */
export const NEAR_LIMIT_RATIO = 0.8;

/**
 * Upgrade nudge for the Members screen. Always shown while on a trial; for paid
 * gyms it appears once usage reaches {@link NEAR_LIMIT_RATIO} of the plan's cap.
 */
export function UpgradeBanner({ used, limit, trial, onUpgrade, className }: {
  used: number;
  limit: number;
  trial: boolean;
  onUpgrade: () => void;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card bg-dark px-4 py-3 text-on-dark', className)}>
      <Sparkles className="size-4 shrink-0 text-accent" aria-hidden />
      <p className="text-sm font-medium">
        {trial ? 'Free trial ended' : 'Members'} · <span className="tabular">{used}/{limit}</span>
      </p>
      <p className="text-sm text-on-dark-muted">
        {trial
          ? 'Choose a plan to add more members.'
          : 'You’re approaching your member limit. Upgrade for more room.'}
      </p>
      <Button size="sm" onClick={onUpgrade} className="ml-auto">
        Upgrade <ArrowUpRight className="size-4" aria-hidden />
      </Button>
    </div>
  );
}

/** Explains why adding members is blocked/limited and how to unlock (manual activation). */
export function UpgradeModal({ open, onClose, trial, limit }: {
  open: boolean;
  onClose: () => void;
  trial: boolean;
  limit: number | null;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={trial ? 'Your free trial has ended' : 'You’ve reached your plan limit'}
      description={
        trial
          ? 'Choose a plan to keep adding members. Your existing members and data stay put.'
          : `Your current plan allows up to ${limit} members.`
      }
      footer={<Button block onClick={onClose}>Got it</Button>}
    >
      <div className="space-y-4 text-sm text-ink-soft">
        <p>
          To add more members, move to a plan with a bigger member cap. Pay securely and your
          cap lifts the moment the payment confirms.
        </p>
        <a
          href="/billing"
          className="flex items-center justify-center gap-2 rounded-control bg-ink px-4 py-2.5 text-sm font-medium text-on-dark transition-colors hover:bg-ink-soft"
        >
          Choose a plan &amp; pay
        </a>
      </div>
    </Modal>
  );
}
